'use client';

import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {X} from 'lucide-react';
import {useMobileRedirectModal} from 'common/hooks/mobile_redirect_modal';
import {SubmissionFlow} from 'iq-interview_experiences/components/submission_flow';

type SubmitExperienceModalProps = {
  onClose: () => void;
  defaultCompanySlug: string;
  defaultPositionSlug: string;
  companyId: number;
  positionId: number;
  guideSlug: string;
};

function SubmitExperienceModal({
  onClose,
  defaultCompanySlug,
  defaultPositionSlug,
  guideSlug,
}: SubmitExperienceModalProps) {
  const [flowSubmitted, setFlowSubmitted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const mounted = useMobileRedirectModal(
    guideSlug ? `/guides/${guideSlug}/submit-experience` : null,
    onClose,
  );

  function requestClose() {
    if (flowSubmitted) {
      window.location.reload();
      onClose();
      return;
    }
    setConfirming(true);
  }

  function confirmClose() {
    setConfirming(false);
    onClose();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (confirming) {
          setConfirming(false);
        } else {
          requestClose();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowSubmitted, confirming]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      className='fixed inset-0 flex items-center justify-center p-4'
      style={{zIndex: 1000}}
      aria-modal='true'
      role='dialog'>
      <div
        className='absolute inset-0 bg-black/60 animate-modal-backdrop'
        onClick={confirming ? undefined : requestClose}
      />

      <div className='relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl animate-modal-panel max-h-[90vh] overflow-y-auto'>
        {/* Confirm-close overlay */}
        {confirming && (
          <div className='absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-white/95 backdrop-blur-sm rounded-2xl px-8 text-center'>
            <p className='text-base font-semibold text-iq-black'>Discard your progress?</p>
            <p className='text-sm text-iq-grey-medium'>
              Your answers won&apos;t be saved if you close now.
            </p>
            <div className='flex gap-3'>
              <button
                onClick={() => setConfirming(false)}
                className='px-5 py-2.5 rounded-lg border border-iq-grey-light text-sm font-medium text-iq-grey-dark hover:border-iq-grey-medium transition-colors'>
                Keep editing
              </button>
              <button
                onClick={confirmClose}
                className='px-5 py-2.5 rounded-lg bg-iq-black text-white text-sm font-medium hover:opacity-80 transition-opacity'>
                Discard
              </button>
            </div>
          </div>
        )}

        <button
          onClick={requestClose}
          className='absolute top-4 right-4 z-10 text-iq-grey-medium hover:text-iq-grey-dark transition-colors'
          aria-label='Close'>
          <X className='w-5 h-5' />
        </button>

        <SubmissionFlow
          compact
          guideSlug={guideSlug}
          defaultCompanySlug={defaultCompanySlug}
          defaultPositionSlug={defaultPositionSlug}
          config={{allowTakeHome: true, allowDiaform: true}}
          onSuccess={() => setFlowSubmitted(true)}
          onDismiss={requestClose}
        />
      </div>
    </div>,
    document.body,
  );
}

export {SubmitExperienceModal};
